import { Image } from 'lucide-react';

// Converts a PDF point value (the certificate PDF is generated at landscape A4,
// 841.89 x 595.28pt) to a `cqw` value (percent of the preview container's width).
// Because the preview container keeps the same 1.414:1 aspect ratio as the page,
// a single factor works for horizontal AND vertical offsets/sizes/fonts.
const PT2CQW = 100 / 841.89;
const px = (pt) => `${pt * PT2CQW}cqw`;

const FONT_FAMILY_CSS = {
  serif: "'Times New Roman', Georgia, serif",
  'sans-serif': "'Helvetica Neue', Arial, sans-serif",
  cursive: "'Brush Script MT', 'Comic Sans MS', cursive",
};

const imageUrl = (backendUrl, url) => (url ? `${backendUrl}${url}` : '');

const SignatoryRow = ({ signatories, textColor, backendUrl, padLeft, padRight }) => {
  if (!signatories.length) return null;
  return (
    <div
      className="absolute flex justify-around"
      style={{ bottom: px(55), left: padLeft, right: padRight }}
    >
      {signatories.map((sig, idx) => (
        <div key={idx} className="text-center flex-1 px-1" style={{ maxWidth: px(160) }}>
          {sig.image_url ? (
            <img src={imageUrl(backendUrl, sig.image_url)} alt={sig.name || `Signatory ${idx + 1}`} style={{ height: px(28) }} className="mx-auto object-contain mb-0.5" />
          ) : (
            <div style={{ height: px(28) }} />
          )}
          <div className="border-t mb-0.5" style={{ borderColor: textColor }} />
          <p className="font-medium truncate" style={{ color: textColor, fontSize: px(11) }}>{sig.name}</p>
          <p className="truncate" style={{ color: textColor, fontSize: px(9) }}>{sig.title}</p>
        </div>
      ))}
    </div>
  );
};

const SealBlock = ({ template, backendUrl, accentColor, textColor, fontFamily, style }) => {
  if (!template.show_seal) return null;
  return (
    <div className="absolute text-center" style={{ width: px(60), ...style }}>
      {template.seal_image_url ? (
        <img src={imageUrl(backendUrl, template.seal_image_url)} alt="Seal" style={{ width: px(60), height: px(60) }} className="object-contain mx-auto" />
      ) : (
        <>
          <p className="font-bold" style={{ color: accentColor, fontFamily, fontSize: px(18) }}>{template.organization_name}</p>
          <p style={{ color: textColor, fontFamily, fontSize: px(9) }}>{template.organization_tagline}</p>
        </>
      )}
    </div>
  );
};

const PhotoBox = ({ template, accentColor, size, style }) => {
  if (!template.show_photo) return null;
  return (
    <div
      className="absolute rounded border-2 flex items-center justify-center bg-white overflow-hidden"
      style={{ width: px(size), height: px(size), borderColor: accentColor, ...style }}
    >
      <Image style={{ width: px(size * 0.35), height: px(size * 0.35) }} className="text-muted-foreground" />
    </div>
  );
};

const photoPositionStyle = (position, size, topPt = 55, sidePt = 55) => {
  const style = { top: px(topPt) };
  if (position === 'top-left') style.left = px(sidePt);
  else if (position === 'top-center') style.left = `calc(50% - ${px(size / 2)})`;
  else style.right = px(sidePt);
  return style;
};

// ==================== LAYOUT: centered_classic / ornate_frame ====================
const CenteredContent = ({ template, previewName, previewCourse, backendUrl, accentColor, textColor, fontFamily, signatories }) => (
  <>
    <div className="flex-1 flex flex-col items-center justify-start text-center relative z-10" style={{ paddingTop: px(60), paddingLeft: px(80), paddingRight: px(80) }}>
      <h1 className="font-bold" style={{ color: accentColor, fontFamily, fontSize: px(template.title_size), marginBottom: px(15) }}>{template.title_text}</h1>
      <p style={{ color: textColor, fontFamily, fontSize: px(18), marginBottom: px(10) }}>{template.subtitle_text}</p>
      <h2 className="font-bold" style={{ color: accentColor, fontFamily, fontSize: px(template.name_size), marginBottom: px(10) }}>{previewName}</h2>
      <p style={{ color: textColor, fontFamily, fontSize: px(18), marginBottom: px(10) }}>{template.completion_text}</p>
      <h3 className="font-bold" style={{ color: textColor, fontFamily, fontSize: px(template.course_size), marginBottom: px(15) }}>{previewCourse}</h3>
      <p style={{ color: textColor, fontFamily, fontSize: px(14) }}>Completed on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      <p style={{ color: textColor, fontFamily, fontSize: px(12), opacity: 0.7, marginTop: px(4) }}>Certificate ID: THAC-{new Date().toISOString().slice(0, 7).replace('-', '')}-PREVIEW</p>
    </div>
    <SignatoryRow signatories={signatories} textColor={textColor} backendUrl={backendUrl} padLeft={px(90)} padRight={px(90)} />
    <SealBlock
      template={template}
      backendUrl={backendUrl}
      accentColor={accentColor}
      textColor={textColor}
      fontFamily={fontFamily}
      style={signatories.length ? { bottom: px(50), left: px(55) } : { bottom: px(45), left: '50%', transform: 'translateX(-50%)' }}
    />
  </>
);

const CornerOrnaments = ({ color }) => {
  const corners = [
    { top: px(46), left: px(46) },
    { top: px(46), right: px(46) },
    { bottom: px(46), left: px(46) },
    { bottom: px(46), right: px(46) },
  ];
  return (
    <>
      {corners.map((pos, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            ...pos,
            width: px(18),
            height: px(18),
            border: `1px solid ${color}`,
            transform: 'rotate(45deg)',
          }}
        />
      ))}
    </>
  );
};

// ==================== LAYOUT: modern_left ====================
const ModernLeftLayout = ({ template, previewName, previewCourse, backendUrl, accentColor, textColor, fontFamily, signatories }) => (
  <>
    <div className="absolute top-0 left-0 bottom-0" style={{ width: px(14), backgroundColor: accentColor }} />
    <div className="absolute text-left" style={{ left: px(70), top: px(60), right: px(160) }}>
      <h1 className="font-bold" style={{ color: accentColor, fontFamily, fontSize: px(Math.min(template.title_size, 40)) }}>{template.title_text}</h1>
      <p style={{ color: textColor, fontFamily, fontSize: px(14), marginTop: px(10) }}>{template.subtitle_text}</p>
      <h2 className="font-bold" style={{ color: accentColor, fontFamily, fontSize: px(Math.min(template.name_size, 44)), marginTop: px(18) }}>{previewName}</h2>
      <p style={{ color: textColor, fontFamily, fontSize: px(14), marginTop: px(10) }}>{template.completion_text}</p>
      <h3 className="font-bold" style={{ color: textColor, fontFamily, fontSize: px(template.course_size), marginTop: px(10) }}>{previewCourse}</h3>
      <p style={{ color: textColor, fontFamily, fontSize: px(12), marginTop: px(12) }}>Completed on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      <p style={{ color: textColor, fontFamily, fontSize: px(12), opacity: 0.7 }}>Certificate ID: THAC-PREVIEW</p>
    </div>
    <PhotoBox template={template} accentColor={accentColor} size={90} style={photoPositionStyle(template.photo_position, 90)} />
    <SignatoryRow signatories={signatories} textColor={textColor} backendUrl={backendUrl} padLeft={px(70)} padRight={px(230)} />
    <SealBlock
      template={template}
      backendUrl={backendUrl}
      accentColor={accentColor}
      textColor={textColor}
      fontFamily={fontFamily}
      style={{ bottom: px(50), right: px(55) }}
    />
  </>
);

// ==================== LAYOUT: banner_top ====================
const BannerTopLayout = ({ template, previewName, previewCourse, backendUrl, accentColor, textColor, fontFamily, signatories }) => (
  <>
    <div className="absolute top-0 left-0 right-0 flex items-center justify-center" style={{ height: px(120), backgroundColor: accentColor }}>
      <h1 className="font-bold text-center px-4" style={{ color: '#FFFFFF', fontFamily, fontSize: px(template.title_size) }}>{template.title_text}</h1>
    </div>
    <div className="absolute left-0 right-0 flex flex-col items-center text-center" style={{ top: px(170), paddingLeft: px(80), paddingRight: px(80) }}>
      <p style={{ color: textColor, fontFamily, fontSize: px(14), marginBottom: px(12) }}>{template.subtitle_text}</p>
      <h2 className="font-bold" style={{ color: accentColor, fontFamily, fontSize: px(template.name_size), marginBottom: px(10) }}>{previewName}</h2>
      <p style={{ color: textColor, fontFamily, fontSize: px(14), marginBottom: px(10) }}>{template.completion_text}</p>
      <h3 className="font-bold" style={{ color: textColor, fontFamily, fontSize: px(template.course_size), marginBottom: px(10) }}>{previewCourse}</h3>
      <p style={{ color: textColor, fontFamily, fontSize: px(12) }}>Completed on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      <p style={{ color: textColor, fontFamily, fontSize: px(12), opacity: 0.7 }}>Certificate ID: THAC-PREVIEW</p>
    </div>
    <PhotoBox template={template} accentColor={accentColor} size={70} style={photoPositionStyle(template.photo_position, 70, 85)} />
    <SignatoryRow signatories={signatories} textColor={textColor} backendUrl={backendUrl} padLeft={px(90)} padRight={px(90)} />
    <SealBlock
      template={template}
      backendUrl={backendUrl}
      accentColor={accentColor}
      textColor={textColor}
      fontFamily={fontFamily}
      style={signatories.length ? { bottom: px(50), left: px(55) } : { bottom: px(45), left: '50%', transform: 'translateX(-50%)' }}
    />
  </>
);

// ==================== LAYOUT: bold_dark ====================
const BoldDarkLayout = ({ template, previewName, previewCourse, backendUrl, accentColor, textColor, fontFamily, signatories }) => (
  <>
    <div className="flex-1 flex flex-col items-center text-center relative z-10" style={{ paddingTop: px(70), paddingLeft: px(60), paddingRight: px(60) }}>
      <h1 className="font-bold" style={{ color: accentColor, fontFamily, fontSize: px(Math.min(template.title_size, 36)), marginBottom: px(15) }}>{template.title_text}</h1>
      <p style={{ color: textColor, fontFamily, fontSize: px(14), marginBottom: px(25) }}>{template.subtitle_text}</p>
      <h2 className="font-bold leading-none" style={{ color: textColor, fontFamily, fontSize: px(template.name_size) }}>{previewName}</h2>
      <div style={{ width: '40%', borderBottom: `${px(2.5)} solid ${accentColor}`, marginTop: px(15), marginBottom: px(20) }} />
      <p style={{ color: textColor, fontFamily, fontSize: px(14), marginBottom: px(10) }}>{template.completion_text}</p>
      <h3 className="font-bold" style={{ color: accentColor, fontFamily, fontSize: px(template.course_size), marginBottom: px(15) }}>{previewCourse}</h3>
      <p style={{ color: textColor, fontFamily, fontSize: px(12) }}>Completed on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      <p style={{ color: textColor, fontFamily, fontSize: px(12), opacity: 0.7, marginTop: px(4) }}>Certificate ID: THAC-PREVIEW</p>
    </div>
    {template.ornament === 'angular' && (
      <>
        <div className="absolute bottom-0 left-0" style={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: `0 0 ${px(40)} ${px(60)}`, borderColor: `transparent transparent transparent ${accentColor}` }} />
        <div className="absolute bottom-0 right-0" style={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: `${px(40)} ${px(60)} 0 0`, borderColor: `${accentColor} transparent transparent transparent` }} />
      </>
    )}
    <SignatoryRow signatories={signatories} textColor={textColor} backendUrl={backendUrl} padLeft={px(90)} padRight={px(90)} />
    <SealBlock
      template={template}
      backendUrl={backendUrl}
      accentColor={accentColor}
      textColor={textColor}
      fontFamily={fontFamily}
      style={signatories.length ? { bottom: px(50), left: px(55) } : { bottom: px(45), left: '50%', transform: 'translateX(-50%)' }}
    />
  </>
);

// ==================== LAYOUT: thachary_curve (Thachari Curve) ====================
// Mirrors backend/utils/certificate_layouts.py TC_HEADER_H / TC_LEFT_W and the
// _layout_thachary_curve drawing order. Page is 841.89 x 595.276pt.
const TC_HEADER_H = 140;
const TC_LEFT_W = 290;
const TC_PAGE_W = 841.89;
const TC_PAGE_H = 595.276;

const ThacharyCurveLayout = ({ template, previewName, previewCourse, backendUrl, signatories }) => {
  const green = template.background_color;
  const textColor = template.text_color;
  const swooshColor = template.accent_color;
  // headerY: PDF y (from bottom) of the header band's lower edge = 455.276
  const headerY = TC_PAGE_H - TC_HEADER_H;

  // Swoosh path — mirrors the backend bezier exactly, converting PDF bottom-up
  // coords to SVG top-down coords via: SVG_y = TC_PAGE_H - PDF_y.
  // Start at top of left panel (x≈280), bulge right to x≈420 at ~40% down,
  // curve back left to bottom-left corner area.
  const swooshPath = [
    `M ${TC_LEFT_W - 10} ${TC_HEADER_H}`,
    `C ${TC_LEFT_W + 150} ${TC_HEADER_H + 5},`,
    `  ${TC_LEFT_W + 170} ${TC_PAGE_H - headerY * 0.55},`,
    `  ${TC_LEFT_W + 130} ${TC_PAGE_H - headerY * 0.40}`,
    `C ${TC_LEFT_W + 100} ${TC_PAGE_H - headerY * 0.22},`,
    `  ${TC_LEFT_W - 60} ${TC_PAGE_H - 30},`,
    `  ${TC_LEFT_W - 90} ${TC_PAGE_H}`,
    `L ${TC_LEFT_W - 120} ${TC_PAGE_H}`,
    `L ${TC_LEFT_W - 120} ${TC_HEADER_H}`,
    'Z',
  ].join(' ');

  const photoSize = 90;
  const showPhoto = template.show_photo;
  const photoReserve = showPhoto ? photoSize + 60 : 0;
  const sig1Only = signatories.length === 1;

  // Approximate course-block bottom for seal positioning (2 rows × 22pt line-height)
  // name_y (PDF) = headerY - 155 → CSS top offset from header bottom = 155
  // course start (PDF) = name_y - 40 = headerY - 195 → CSS top offset = 195
  // 2 rows at 22pt → course bottom CSS top offset ≈ 195 + 44 = 239
  // seal center CSS top offset ≈ 239 + 40 + 25 = 304 (25 = seal_size/2)
  const sealCssTopOffset = 304;

  return (
    <>
      {/* Left green panel (below header) */}
      <div className="absolute left-0 bottom-0" style={{ top: px(TC_HEADER_H), width: px(TC_LEFT_W), backgroundColor: green }} />
      {/* Top header band */}
      <div className="absolute top-0 left-0 right-0" style={{ height: px(TC_HEADER_H), backgroundColor: green }} />
      {/* Swoosh — proper bezier matching the backend path */}
      <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${TC_PAGE_W} ${TC_PAGE_H}`} preserveAspectRatio="none">
        <path d={swooshPath} fill={swooshColor} />
      </svg>

      {/* Logo + org name/tagline beside it */}
      <div className="absolute flex items-center" style={{ left: px(26), top: px(28), height: px(56) }}>
        {template.logo_url ? (
          <img src={imageUrl(backendUrl, template.logo_url)} alt="Logo" style={{ width: px(56), height: px(56) }} className="object-contain shrink-0" />
        ) : (
          <div className="flex items-center justify-center font-bold shrink-0" style={{ width: px(56), height: px(56), backgroundColor: '#d9d9d9', borderRadius: px(8), fontSize: px(26) }}>
            <span style={{ color: '#c0392b' }}>T</span><span style={{ color: '#1a1a1a' }}>R</span>
          </div>
        )}
        <div style={{ marginLeft: px(14), maxWidth: px(190) }}>
          <p className="font-bold truncate" style={{ color: '#fff', fontSize: px(15) }}>{template.organization_name}</p>
          <p className="truncate" style={{ color: '#fff', fontSize: px(9) }}>{template.organization_tagline}</p>
        </div>
      </div>

      {/* Header title — right side of header band, vertically centered */}
      <div className="absolute flex flex-col items-center justify-center" style={{ left: px(TC_LEFT_W), right: 0, top: 0, height: px(TC_HEADER_H) }}>
        <p className="font-bold leading-tight" style={{ color: '#fff', fontSize: px(template.title_size) }}>{template.title_text}</p>
        <p className="font-bold leading-tight" style={{ color: '#fff', fontSize: px(template.title_size) }}>{template.subtitle_text}</p>
      </div>

      {/* Left panel optional text (seal moved to content area) */}
      {template.left_panel_text && (
        <div className="absolute text-center px-2" style={{ left: 0, width: px(TC_LEFT_W), top: px(TC_HEADER_H + 30), color: '#fff', fontSize: px(11) }}>
          {template.left_panel_text}
        </div>
      )}

      {/* Presentation line — 30pt below header band bottom, shifted left when photo shown */}
      <div
        className="absolute text-center"
        style={{
          left: px(TC_LEFT_W),
          right: showPhoto ? px(photoReserve) : 0,
          top: px(TC_HEADER_H + 30),
          padding: `0 ${px(40)}`,
        }}
      >
        <p className="font-bold" style={{ color: textColor, fontSize: px(13) }}>{template.presentation_text}</p>
      </div>

      {/* Recipient name + rule line — ~120pt below presentation line */}
      <div className="absolute text-center" style={{ left: px(TC_LEFT_W), right: 0, top: px(TC_HEADER_H + 147), padding: `0 ${px(80)}` }}>
        <p className="font-bold truncate pb-1 border-b" style={{ color: textColor, fontSize: px(template.name_size), borderColor: textColor }}>{previewName}</p>
      </div>

      {/* Course completion text — 40pt below name rule line */}
      <div className="absolute text-center" style={{ left: px(TC_LEFT_W), right: 0, top: px(TC_HEADER_H + 195), padding: `0 ${px(50)}` }}>
        <p style={{ color: textColor, fontSize: px(14) }}>{template.completion_text}</p>
        <p className="font-bold truncate" style={{ color: textColor, fontSize: px(template.course_size) }}>{previewCourse}</p>
      </div>

      {/* Seal — centered in content area, 40pt below course text block */}
      {template.show_seal && template.seal_image_url && (
        <img
          src={imageUrl(backendUrl, template.seal_image_url)}
          alt="Seal"
          className="absolute object-contain"
          style={{
            width: px(50),
            height: px(50),
            top: px(TC_HEADER_H + sealCssTopOffset),
            left: `calc(50% + ${px(TC_LEFT_W / 2)} - ${px(25)})`,
          }}
        />
      )}
      {/* Seal placeholder when no image but seal is toggled on */}
      {template.show_seal && !template.seal_image_url && (
        <div
          className="absolute flex flex-col items-center justify-center text-center"
          style={{
            width: px(50),
            height: px(50),
            top: px(TC_HEADER_H + sealCssTopOffset),
            left: `calc(50% + ${px(TC_LEFT_W / 2)} - ${px(25)})`,
            border: `1px solid ${textColor}`,
            borderRadius: '50%',
            opacity: 0.3,
          }}
        />
      )}

      {/* Date of issue — bottom-left of content area */}
      <div className="absolute" style={{ left: px(TC_LEFT_W + 55), bottom: px(34) }}>
        <p style={{ color: textColor, fontSize: px(10) }}>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        <div style={{ width: px(130), borderBottom: `1px solid ${textColor}`, margin: `${px(2)} 0` }} />
        <p className="font-bold" style={{ color: textColor, fontSize: px(9) }}>{template.date_label}</p>
      </div>

      {/* Signatories — pinned to bottom, 40pt margin from page bottom */}
      <SignatoryRow
        signatories={signatories}
        textColor={textColor}
        backendUrl={backendUrl}
        padLeft={sig1Only ? '60%' : px(TC_LEFT_W + 180)}
        padRight={px(50)}
      />

      {/* User photo — top-right of content area, 30pt below header band.
          Show placeholder box only when show_photo is toggled ON (no image → grey box).
          When show_photo is OFF: nothing rendered. */}
      {showPhoto && (
        <PhotoBox
          template={template}
          accentColor={swooshColor}
          size={photoSize}
          style={{ top: px(TC_HEADER_H + 30), right: px(50) }}
        />
      )}
    </>
  );
};

/**
 * Renders a scaled live preview of a certificate template, mirroring the
 * reportlab layout engines in backend/utils/certificate_layouts.py.
 * Sizes/positions are expressed in `cqw` so the same markup can be reused
 * at any size (large designer preview or small gallery card) — the wrapper
 * must have `containerType: 'inline-size'` set (done here).
 */
export const CertificatePreview = ({ template, previewName = 'John Doe', previewCourse = 'Introduction to Interior Design', backendUrl = '' }) => {
  const layout = template.layout || 'centered_classic';
  const fontFamily = FONT_FAMILY_CSS[template.font_family] || FONT_FAMILY_CSS.serif;
  const accentColor = template.accent_color;
  const textColor = template.text_color;
  const borderColor = template.border_color;
  const signatories = (template.signatories || []).filter((s) => s.show !== false && (s.name || s.image_url)).slice(0, 3);

  const sharedProps = { template, previewName, previewCourse, backendUrl, accentColor, textColor, fontFamily, signatories };

  // For thachary_curve, background_color/border_color are the green panels / white
  // content area respectively, so the container itself should start out white.
  const containerBg = layout === 'thachary_curve' ? template.border_color : template.background_color;

  return (
    <div
      className="aspect-[1.414/1] rounded-lg overflow-hidden shadow-lg relative w-full"
      style={{ backgroundColor: containerBg, containerType: 'inline-size' }}
    >
      <div className="w-full h-full flex flex-col relative">
        {layout === 'thachary_curve' && <ThacharyCurveLayout template={template} previewName={previewName} previewCourse={previewCourse} backendUrl={backendUrl} signatories={signatories} />}

        {/* Border(s) */}
        {template.show_border && layout !== 'thachary_curve' && (
          <>
            <div className="absolute pointer-events-none rounded" style={{ inset: px(30), border: `${px(3)} solid ${borderColor}` }} />
            <div className="absolute pointer-events-none rounded" style={{ inset: px(40), border: `${px(1)} solid ${borderColor}` }} />
            {layout === 'ornate_frame' && (
              <>
                <div className="absolute pointer-events-none rounded" style={{ inset: px(24), border: `${px(4)} solid ${borderColor}` }} />
                <div className="absolute pointer-events-none rounded" style={{ inset: px(36), border: `${px(1.5)} solid ${borderColor}` }} />
              </>
            )}
          </>
        )}
        {layout === 'ornate_frame' && template.ornament === 'flourish' && <CornerOrnaments color={borderColor} />}

        {layout === 'modern_left' && <ModernLeftLayout {...sharedProps} />}
        {layout === 'banner_top' && <BannerTopLayout {...sharedProps} />}
        {layout === 'bold_dark' && <BoldDarkLayout {...sharedProps} />}
        {(layout === 'centered_classic' || layout === 'ornate_frame') && <CenteredContent {...sharedProps} />}

        {(layout === 'centered_classic' || layout === 'ornate_frame' || layout === 'bold_dark') && (
          <PhotoBox template={template} accentColor={accentColor} size={80} style={photoPositionStyle(template.photo_position, 80)} />
        )}
      </div>
    </div>
  );
};

export default CertificatePreview;
